import { FilterOptionRow, FilterSection } from "@/components/filters/FilterPanel";
import {
  countSelectedRows,
  getTagDisplayLabel,
  getTagSelectionState,
  type FilterSelectionState,
} from "./documentFilterRowUtils";
import type { Tag, TagGroup } from "./types";

interface FilterGroupAccordionItemProps {
  group: TagGroup;
  selectedTags: string[];
  onToggle: (filterId: string) => void;
  onClear: () => void;
  clearLabel: string;
}

interface FilterTagRowProps {
  group: TagGroup;
  tag: Tag;
  selectionState: FilterSelectionState;
  showGroupLabel?: boolean;
  onToggle: (filterId: string) => void;
}

export function FilterTagRow({
  group,
  tag,
  selectionState,
  showGroupLabel = false,
  onToggle,
}: FilterTagRowProps) {
  const displayLabel = getTagDisplayLabel(group, tag);
  return (
    <FilterOptionRow
      label={displayLabel}
      ariaLabel={`${group.label}: ${displayLabel}`}
      checked={selectionState === "checked"}
      indeterminate={selectionState === "indeterminate"}
      onToggle={() => onToggle(tag.id)}
      count={tag.count}
      sublabel={showGroupLabel ? group.label : undefined}
      indent={!showGroupLabel && tag.isSubcategory}
    />
  );
}

interface FilterRow {
  tag: Tag;
  selectionState: FilterSelectionState;
}

/**
 * Split the group's rows into blocks of a parent followed by its
 * subcategories, in the hierarchical order `useTagGroups` built. Hoisting
 * moves a whole block, so a `pl-6` child can never end up indented under an
 * unrelated tag: a partly-selected parent takes its subtree up with it.
 */
function groupRowsByParent(rows: FilterRow[]): FilterRow[][] {
  return rows.reduce<FilterRow[][]>((blocks, row) => {
    const currentBlock = blocks[blocks.length - 1];
    if (row.tag.isSubcategory && currentBlock) currentBlock.push(row);
    else blocks.push([row]);
    return blocks;
  }, []);
}

function FilterTagList({
  group,
  selectedTags,
  onToggle,
}: Pick<FilterGroupAccordionItemProps, "group" | "selectedTags" | "onToggle">) {
  const blocks = groupRowsByParent(
    group.tags.map((tag) => ({
      tag,
      selectionState: getTagSelectionState(tag, selectedTags),
    }))
  );
  const hasSelection = (block: FilterRow[]) =>
    block.some((row) => row.selectionState !== "unchecked");
  const selectedRows = blocks.filter(hasSelection).flat();
  const unselectedRows = blocks.filter((block) => !hasSelection(block)).flat();

  return (
    <>
      {selectedRows.map((row) => (
        <FilterTagRow key={row.tag.id} group={group} onToggle={onToggle} {...row} />
      ))}
      {selectedRows.length > 0 && unselectedRows.length > 0 && (
        // A gap, not a rule: the filter panel carries no dividing lines.
        <div role="separator" className="my-1.5" />
      )}
      {unselectedRows.map((row) => (
        <FilterTagRow key={row.tag.id} group={group} onToggle={onToggle} {...row} />
      ))}
    </>
  );
}

export function FilterGroupAccordionItem({
  group,
  selectedTags,
  onToggle,
  onClear,
  clearLabel,
}: FilterGroupAccordionItemProps) {
  return (
    <FilterSection
      value={group.id}
      icon={group.icon}
      label={group.label}
      selectedCount={countSelectedRows(selectedTags, [group])}
      onClear={onClear}
      clearLabel={clearLabel}
    >
      <FilterTagList group={group} selectedTags={selectedTags} onToggle={onToggle} />
    </FilterSection>
  );
}
